import { useEffect } from 'react';

import type { CfFlowResponse, CfFlowScreen } from './api/cloudflareFlowApi';
import { type CfEventSubject, cfEventSubjectOf } from './cfEventProperties';
import {
  CfStepName,
  FlowIdentity,
  NEVER_LEFT_GAP_MS,
  StepEvent,
  StepRecord,
  cfStepName,
  decideStepEvents,
  parseStepRecord,
} from './cfStepTrail';
import { useCfFlowEventTracker } from './useCfFlowEventTracker';

export const STEP_VIEWED_EVENT = 'Automated CF Integration: Step Viewed';
export const STEP_COMPLETED_EVENT = 'Automated CF Integration: Step Completed';

const EVENT_NAME: Readonly<Record<StepEvent['type'], string>> = {
  viewed: STEP_VIEWED_EVENT,
  completed: STEP_COMPLETED_EVENT,
};

/** The last step this browser showed for the domain; shared by every tab. */
export const lastStepKey = (domain: string): string => `cfLastStep:${domain}`;

const readLastStep = (domain: string): StepRecord | null => parseStepRecord(window.localStorage.getItem(lastStepKey(domain)));

const writeLastStep = (domain: string, record: StepRecord): void =>
  window.localStorage.setItem(lastStepKey(domain), JSON.stringify(record));

const STILL_SHOWN_INTERVAL_MS = NEVER_LEFT_GAP_MS / 5;
const STILL_SHOWN_EVENTS = [
  [window, 'pagehide'],
  [document, 'visibilitychange'],
] as const;

type StepOnScreen = {
  subject: CfEventSubject | undefined;
  domain: string | undefined;
  flow: FlowIdentity;
  subCase?: CfFlowResponse['subCase'];
  step: CfStepName | null;
};

const useStepOnScreenEvents = ({ subject, domain, flow, subCase, step }: StepOnScreen): void => {
  const track = useCfFlowEventTracker(subject);
  useEffect(() => {
    if (!domain || step === null) return;

    const decision = decideStepEvents({ recorded: readLastStep(domain), step, flow, subCase }, Date.now());

    writeLastStep(domain, decision.record);
    decision.events.forEach((event) => track(EVENT_NAME[event.type], { step_name: event.step }));
  }, [domain, flow, subCase, step]);

  useEffect(() => {
    if (!domain || step === null) return;

    const completedInThisFlow = () => {
      const recorded = readLastStep(domain);

      return recorded?.flow === flow ? recorded.completed : [];
    };
    const markStillShown = () => writeLastStep(domain, { step, at: Date.now(), completed: completedInThisFlow(), flow });
    const interval = window.setInterval(markStillShown, STILL_SHOWN_INTERVAL_MS);
    STILL_SHOWN_EVENTS.forEach(([target, type]) => target.addEventListener(type, markStillShown));

    return () => {
      window.clearInterval(interval);
      STILL_SHOWN_EVENTS.forEach(([target, type]) => target.removeEventListener(type, markStillShown));
    };
  }, [domain, flow, step]);
};

const stepViewedOnFlowScreen = (screen: CfFlowScreen, status: CfFlowResponse['status']): CfStepName | null => {
  const step = cfStepName(screen, status);

  return step === 'blocked' ? null : step;
};

export const useCfStepEvents = (
  flow: CfFlowResponse | undefined,
  shownScreen: CfFlowScreen | null | undefined = flow?.screen,
): void =>
  useStepOnScreenEvents({
    subject: flow && cfEventSubjectOf(flow),
    domain: flow?.domain,
    flow: flow?.createdAt ?? null,
    subCase: flow?.subCase,
    step: flow && shownScreen ? stepViewedOnFlowScreen(shownScreen, flow.status) : null,
  });

/** For a screen shown before or outside the flow, which has no step to complete. */
export const useCfStepViewed = (step: CfStepName, subject: CfEventSubject): void =>
  useStepOnScreenEvents({ subject, domain: subject.domain, flow: null, step });
